import type {
  AdminRoyaltyReportCountsResponse,
  AdminRoyaltyReportResponse,
} from '@inflip/contracts/admin';
import {
  IP_RIGHT_LABELS,
  RoyaltyReportKind,
  RoyaltyReportStatus,
  rateBpLabel,
  formatInTimeZone,
} from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { AnimatedCheckbox } from '../components/animated-checkbox';
import { ConfirmDepositDialog } from '../components/confirm-deposit-dialog';
import { DateRangeFilter } from '../components/date-range-filter';
import {
  Button,
  Chip,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorBanner,
  PageHeader,
  Pager,
  PromptDialog,
  inputCls,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { ROYALTY_STATUS_META } from '../lib/status-ui';
import { adminTimeZone, formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';
import { notifyPendingWorkChanged } from '../lib/use-pending-work';

type DialogState =
  | null
  | { kind: 'confirm'; report: AdminRoyaltyReportResponse }
  /** 입금 확인(여러 달 묶음 가능) — 같은 계약의 입금 대기 보고들 */
  | { kind: 'deposit'; reports: AdminRoyaltyReportResponse[] }
  | { kind: 'payout'; report: AdminRoyaltyReportResponse }
  | { kind: 'void'; report: AdminRoyaltyReportResponse }
  | { kind: 'adjust'; report: AdminRoyaltyReportResponse }
  | { kind: 'dismiss'; report: AdminRoyaltyReportResponse; objectionId: string };

/** 상태 칩 외 특수 필터 — 판매자 이의가 열린 보고 */
const OBJECTION_FILTER = 'OBJECTION';

/** 로열티 정산 큐 — 입금 확인 → 판매자 지급 */
export function RoyaltiesPage() {
  const { session } = useAdminAuth();
  const [filter, setFilter] = useListViewState<RoyaltyReportStatus | '' | typeof OBJECTION_FILTER>(
    'status',
    RoyaltyReportStatus.REPORTED,
  );
  // 검색어 — 주문 번호 앞자리, 상품 이름, 구매자·판매자 이름·이메일
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  // 기간 — 매출 보고일 기준. 기본은 전체 (처리할 건이 기간 밖으로 숨지 않게)
  const [dateFrom, setDateFrom] = useListViewState('from', '');
  const [dateTo, setDateTo] = useListViewState('to', '');
  const [page, setPage] = useListViewState('page', 1);
  const [rows, setRows] = useState<AdminRoyaltyReportResponse[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  // 처리 대기 개수 — 입금 확인 대기·지급 대기·판매자 이의 칩에 붙인다
  const [counts, setCounts] = useState<AdminRoyaltyReportCountsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);
  // 묶음 입금 확인용 선택 — 같은 계약(주문)의 입금 대기 보고만 함께 고를 수 있다
  const [selected, setSelected] = useState<string[]>([]);
  const selectedRows = rows.filter((r) => selected.includes(r.id));
  const selectedOrder = selectedRows[0]?.orderId ?? null;
  const depositable = (r: AdminRoyaltyReportResponse) =>
    r.status === RoyaltyReportStatus.REPORTED && r.royaltyAmount > 0;

  const load = useCallback(() => {
    if (!session) return Promise.resolve();
    // 개수는 실패해도 목록을 막지 않는다
    void api
      .royaltyReportCounts(session.accessToken)
      .then(setCounts)
      .catch(() => undefined);
    return api
      .listRoyaltyReports(session.accessToken, {
        ...(filter === OBJECTION_FILTER
          ? { openObjection: true }
          : filter
            ? { status: filter }
            : {}),
        ...(searchTerm ? { q: searchTerm } : {}),
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        page,
      })
      .then((r) => {
        setRows(r.items);
        setTotalPages(r.totalPages);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [session, filter, searchTerm, dateFrom, dateTo, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await fn();
      setDialog(null);
      setSelected([]);
      await load();
      // 사이드바 빨간 점도 바로 다시 센다
      notifyPendingWorkChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 그사이 다른 어드민·회원이 상태를 바꿔 거절됐을 수 있다 — 지금 상태로 다시 그린다 (입금 확인 창은 오류를 창 안에 보여 주므로 열어 둔다)
      setDialog((d) => (d?.kind === 'deposit' ? d : null));
      await load();
    } finally {
      setPending(false);
    }
  }

  function changeFilter(next: typeof filter) {
    setPage(1);
    setFilter(next);
  }

  if (!session) return null;
  const token = session.accessToken;

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="로열티 정산"
        description="로열티 입금 확인과 판매자 지급을 처리해요 · 기간은 매출 보고일 기준"
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-72 ${inputCls}`}
              placeholder="주문 번호 / 상품 / 구매자·판매자 이름·이메일"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        <Chip active={filter === ''} onClick={() => changeFilter('')}>
          전체
        </Chip>
        {Object.entries(ROYALTY_STATUS_META).map(([k, v]) => (
          <Chip
            key={k}
            active={filter === k}
            onClick={() => changeFilter(k as RoyaltyReportStatus)}
          >
            {v.label}
            <ChipCount
              value={
                k === RoyaltyReportStatus.REPORTED
                  ? counts?.reported
                  : k === RoyaltyReportStatus.CONFIRMED
                    ? counts?.confirmed
                    : undefined
              }
            />
          </Chip>
        ))}
        <Chip active={filter === OBJECTION_FILTER} onClick={() => changeFilter(OBJECTION_FILTER)}>
          판매자 이의
          <ChipCount value={counts?.openObjection} />
        </Chip>
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={(from, to) => {
            setPage(1);
            setDateFrom(from);
            setDateTo(to);
          }}
        />
      </div>

      {error && <ErrorBanner message={error} />}

      {rows.length === 0 && (
        <EmptyState message={searchTerm ? '검색 결과가 없어요.' : '해당 상태의 보고가 없어요.'} />
      )}

      <div className="divide-y divide-border/60">
        {rows.map((r) => {
          // 단계마다 지금 챙길 쪽을 진하게 — 입금 확인 대기는 구매자(받았는지), 지급 대기는 판매자(보낼 곳)
          const awaitingDeposit = r.status === RoyaltyReportStatus.REPORTED;
          const awaitingPayout = r.status === RoyaltyReportStatus.CONFIRMED;
          // 정정분(차액) — 음수면 구매자 환급·판매자 회수 방향
          const isAdjustment = r.kind === RoyaltyReportKind.ADJUSTMENT;
          const refund = r.royaltyAmount < 0;
          const clawback = r.sellerPayoutAmount < 0;
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1.5 py-4 text-sm">
              {/* 입금 대기 보고만 고를 수 있다 — 다른 계약을 고른 중이면 잠긴다 */}
              {depositable(r) && (
                <AnimatedCheckbox
                  aria-label={`${periodLabel(r.period)} 입금 확인에 넣기`}
                  title={
                    selectedOrder && selectedOrder !== r.orderId
                      ? '같은 계약의 보고만 함께 입금 확인할 수 있어요'
                      : undefined
                  }
                  checked={selected.includes(r.id)}
                  disabled={pending || (!!selectedOrder && selectedOrder !== r.orderId)}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked ? [...selected, r.id] : selected.filter((id) => id !== r.id),
                    )
                  }
                />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={`text-xs font-bold ${ROYALTY_STATUS_META[r.status].cls}`}>
                    {ROYALTY_STATUS_META[r.status].label}
                  </span>
                  {isAdjustment && <span className="text-xs font-bold text-foreground">정정</span>}
                  {/* 누가 반려했는지 — 판매자(매출이 다르다고 판단) / 관리자 */}
                  {r.status === RoyaltyReportStatus.VOIDED && (
                    <span className="text-xs font-bold text-rose-600">
                      {r.voidedByRole === 'SELLER' ? '판매자 반려' : '관리자 반려'}
                    </span>
                  )}
                  {/* 구매자의 빠른 입금 확인 요청 — 은행 내역을 먼저 확인할 건 */}
                  {awaitingDeposit && r.depositNotifiedAt && (
                    <span className="text-xs font-bold text-rose-600">
                      입금했다고 알림 ·{' '}
                      {formatInTimeZone(r.depositNotifiedAt, adminTimeZone(), {
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                  <span className="text-xs font-medium tabular-nums">{periodLabel(r.period)}</span>
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: r.orderId }}
                    className="font-mono text-xs font-medium text-brand hover:underline"
                  >
                    주문 {r.orderId.slice(0, 8)}
                  </Link>
                  <span className="min-w-0 truncate text-xs font-semibold">
                    {r.listingTitle ?? '(삭제된 상품)'}
                    <span className="ml-1 font-medium text-muted-foreground">
                      {IP_RIGHT_LABELS[r.rightType]}
                    </span>
                  </span>
                </div>
                {/* 돈의 방향 — 구매자가 로열티를 입금하고, 플랫폼이 판매자에게 지급분을 보낸다 */}
                <p className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
                  <span className="text-muted-foreground">입금</span>
                  <PersonLink
                    id={r.buyerId}
                    name={r.buyerName}
                    email={r.buyerEmail}
                    role="구매자"
                    dimmed={awaitingPayout}
                  />
                  <span aria-hidden className="text-muted-foreground">
                    →
                  </span>
                  <span className="text-muted-foreground">지급</span>
                  <PersonLink
                    id={r.sellerId}
                    name={r.sellerName ?? ''}
                    email={r.sellerEmail}
                    role="판매자"
                    dimmed={awaitingDeposit}
                  />
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isAdjustment ? '바로잡은 매출 ' : '매출 '}
                  {r.reportedRevenue.toLocaleString('ko-KR')}원 × {rateBpLabel(r.royaltyRateBp)} ·{' '}
                  {isAdjustment ? '판매자 지급분 차액 ' : '판매자 지급분 '}
                  {signed(r.sellerPayoutAmount, isAdjustment)}원 ·{' '}
                  {/* 지급 대기에서는 보낼 계좌가 핵심이라 진하게, 그 전에는 미리 알아 두는 정도로 흐리게 */}
                  <span
                    className={
                      awaitingPayout
                        ? r.sellerPayoutAccount
                          ? 'font-semibold text-foreground'
                          : 'font-semibold text-rose-600'
                        : awaitingDeposit
                          ? 'text-muted-foreground/70'
                          : undefined
                    }
                  >
                    {r.sellerPayoutAccount
                      ? `${r.sellerPayoutAccount.bankName} ${r.sellerPayoutAccount.accountNumber}`
                      : '⚠️ 계좌 미등록'}
                  </span>
                </p>
                <ReportFiles token={token} report={r} onError={setError} />
                {r.objections.map((o) => (
                  <p
                    key={o.id}
                    className={`mt-1 text-xs ${o.status === 'OPEN' ? 'font-semibold text-rose-600' : 'text-muted-foreground'}`}
                  >
                    {o.status === 'OPEN'
                      ? '판매자 이의 — 확인 필요'
                      : o.status === 'ADJUSTED'
                        ? '판매자 이의 — 정정함'
                        : '판매자 이의 — 기각함'}
                    {' · '}
                    {o.reason}
                    {o.resolution && ` · 처리: ${o.resolution}`}
                  </p>
                ))}
                {(r.voidReason || (isAdjustment && r.note)) && (
                  <p
                    className={`mt-1 text-xs ${r.voidReason ? 'text-rose-600' : 'text-foreground'}`}
                  >
                    {r.voidReason
                      ? `${r.voidedByRole === 'SELLER' ? '판매자' : '관리자'} 반려 사유: ${r.voidReason}`
                      : `정정 근거: ${r.note}`}
                  </p>
                )}
                <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                  {isAdjustment ? '정정' : '보고'} {formatDateTime(r.createdAt)}
                  {r.confirmedAt && ` · 입금 확인 ${formatDateTime(r.confirmedAt)}`}
                  {r.paidOutAt && ` · 지급 ${formatDateTime(r.paidOutAt)}`}
                </p>
              </div>
              {/* 오른쪽 금액도 단계에 맞춘다 — 지급 대기는 판매자에게 보낼 지급분 */}
              <div className="shrink-0 text-right">
                <p className="text-[11px] text-muted-foreground">
                  {awaitingDeposit
                    ? refund
                      ? '구매자에게 돌려줄 금액'
                      : '받을 로열티'
                    : awaitingPayout
                      ? clawback
                        ? '판매자에게서 회수할 금액'
                        : '보낼 지급분'
                      : '로열티'}
                </p>
                <p className="text-base font-extrabold tabular-nums tracking-tight">
                  {Math.abs(awaitingPayout ? r.sellerPayoutAmount : r.royaltyAmount).toLocaleString(
                    'ko-KR',
                  )}
                  원
                </p>
              </div>
              <div className="flex shrink-0 gap-1.5">
                {r.status === RoyaltyReportStatus.REPORTED && (
                  <>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={pending}
                      onClick={() => setDialog({ kind: 'void', report: r })}
                    >
                      반려
                    </Button>
                    <Button
                      size="sm"
                      disabled={pending}
                      onClick={() =>
                        setDialog(
                          refund
                            ? { kind: 'confirm', report: r }
                            : { kind: 'deposit', reports: [r] },
                        )
                      }
                    >
                      {refund ? '환급 완료' : '입금 확인'}
                    </Button>
                  </>
                )}
                {r.status === RoyaltyReportStatus.CONFIRMED && (
                  <Button
                    size="sm"
                    disabled={pending}
                    onClick={() => setDialog({ kind: 'payout', report: r })}
                  >
                    {clawback ? '회수 완료' : '지급 완료'}
                  </Button>
                )}
                {/* 판매자 이의 — 확인해 보고가 맞으면 기각, 틀리면 아래 정정 */}
                {r.objections
                  .filter((o) => o.status === 'OPEN')
                  .map((o) => (
                    <Button
                      key={o.id}
                      size="sm"
                      variant="danger"
                      disabled={pending}
                      onClick={() => setDialog({ kind: 'dismiss', report: r, objectionId: o.id })}
                    >
                      이의 기각
                    </Button>
                  ))}
                {/* 입금 확인 이후 매출이 틀렸던 게 확인되면 정정(차액 보고) */}
                {!isAdjustment &&
                  (r.status === RoyaltyReportStatus.CONFIRMED ||
                    r.status === RoyaltyReportStatus.PAID_OUT) && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={pending}
                      onClick={() => setDialog({ kind: 'adjust', report: r })}
                    >
                      정정
                    </Button>
                  )}
              </div>
            </div>
          );
        })}
      </div>

      <Pager page={page} totalPages={totalPages} onChange={setPage} />

      {/* 고른 보고 묶음 — 아래 고정 막대에서 한 번에 입금 확인 */}
      {selectedRows.length > 0 && (
        <div className="sticky bottom-0 z-10 -mx-6 mt-4 flex flex-wrap items-center gap-3 border-t border-border bg-background/95 px-6 py-3 backdrop-blur">
          <span className="text-sm">
            <span className="font-bold">{selectedRows[0]!.listingTitle ?? '(삭제된 상품)'}</span>
            <span className="ml-2 text-muted-foreground">
              {selectedRows.map((r) => periodLabel(r.period).replace(' 매출분', '')).join(', ')} ·{' '}
              {selectedRows.length}건
            </span>
          </span>
          <span className="ml-auto text-base font-extrabold tabular-nums">
            {selectedRows.reduce((sum, r) => sum + r.royaltyAmount, 0).toLocaleString('ko-KR')}원
          </span>
          <Button variant="secondary" size="sm" onClick={() => setSelected([])}>
            선택 해제
          </Button>
          <Button
            size="sm"
            disabled={pending}
            onClick={() => setDialog({ kind: 'deposit', reports: selectedRows })}
          >
            함께 입금 확인
          </Button>
        </div>
      )}

      {dialog?.kind === 'deposit' && (
        <ConfirmDepositDialog
          title={
            dialog.reports.length > 1
              ? `로열티 ${dialog.reports.length}건 입금 확인`
              : '로열티 입금 확인'
          }
          expectedAmount={dialog.reports.reduce((sum, r) => sum + r.royaltyAmount, 0)}
          expectedDepositor={dialog.reports[0]!.buyerDepositorName}
          note={`${dialog.reports
            .map((r) => periodLabel(r.period))
            .join(', ')} 로열티가 입금 확인되고 판매자 지급 대기로 넘어가요.`}
          busy={pending}
          error={error}
          onSubmit={(input) =>
            void run(() =>
              api.confirmRoyaltyDeposit(token, {
                reportIds: dialog.reports.map((r) => r.id),
                receivedAmount: input.receivedAmount,
                depositorName: input.depositorName,
                ...(input.overrideReason ? { depositorOverrideReason: input.overrideReason } : {}),
              }),
            )
          }
          onClose={() => {
            setDialog(null);
            setError(null);
          }}
        />
      )}
      {dialog?.kind === 'confirm' && (
        <ConfirmDialog
          title={dialog.report.royaltyAmount < 0 ? '구매자에게 환급했나요?' : '입금을 확인했나요?'}
          description={
            dialog.report.royaltyAmount < 0
              ? `과다 납부된 로열티 ${Math.abs(dialog.report.royaltyAmount).toLocaleString('ko-KR')}원을 구매자에게 돌려준 뒤 눌러주세요.`
              : `로열티 ${dialog.report.royaltyAmount.toLocaleString('ko-KR')}원이 입금됐는지 확인한 뒤 눌러주세요.`
          }
          confirmLabel={dialog.report.royaltyAmount < 0 ? '환급 완료' : '입금 확인'}
          busy={pending}
          onConfirm={() => void run(() => api.confirmRoyaltyReport(token, dialog.report.id))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'payout' && (
        <ConfirmDialog
          title={
            dialog.report.sellerPayoutAmount < 0
              ? '판매자에게서 회수했나요?'
              : '지급 완료 처리할까요?'
          }
          description={
            dialog.report.sellerPayoutAmount < 0
              ? `과다 지급된 ${Math.abs(dialog.report.sellerPayoutAmount).toLocaleString('ko-KR')}원을 판매자에게서 돌려받은 뒤 눌러주세요.`
              : `판매자에게 ${dialog.report.sellerPayoutAmount.toLocaleString('ko-KR')}원 — 실제 송금을 마친 뒤 눌러주세요.${
                  dialog.report.kind === RoyaltyReportKind.ORIGINAL
                    ? ' 이미 확인된 정정분이 있으면 합산한 금액으로 지급 처리돼요.'
                    : ''
                }`
          }
          confirmLabel={dialog.report.sellerPayoutAmount < 0 ? '회수 완료' : '지급 완료'}
          busy={pending}
          onConfirm={() => void run(() => api.payOutRoyaltyReport(token, dialog.report.id))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'void' && (
        <PromptDialog
          title="이 보고를 반려할까요?"
          description="반려하면 구매자·판매자에게 사유가 전달되고, 원래 보고라면 구매자가 같은 달을 다시 보고할 수 있어요."
          placeholder="반려 사유 (5자 이상)"
          submitLabel="반려"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.voidRoyaltyReport(token, dialog.report.id, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'dismiss' && (
        <PromptDialog
          title="판매자 이의를 기각할까요?"
          description="구매자 증빙과 판매자 자료를 확인해 보고가 맞을 때 기각해요. 사유는 판매자·구매자에게 전달돼요. 보고가 틀렸다면 기각 대신 '정정'을 눌러 주세요."
          placeholder="기각 사유 (5자 이상)"
          submitLabel="기각"
          danger
          busy={pending}
          onSubmit={(reason) =>
            void run(() => api.dismissRoyaltyObjection(token, dialog.objectionId, { reason }))
          }
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'adjust' && (
        <AdjustDialog
          report={dialog.report}
          busy={pending}
          onSubmit={(body) =>
            void run(() => api.adjustRoyaltyReport(token, dialog.report.id, body))
          }
          onClose={() => setDialog(null)}
        />
      )}
    </main>
  );
}

/** 첨부 — 구매자 증빙 / 판매자 이의 자료. 누르면 새 탭으로 연다 */
function ReportFiles({
  token,
  report,
  onError,
}: {
  token: string;
  report: AdminRoyaltyReportResponse;
  onError(message: string): void;
}) {
  if (report.files.length === 0) {
    return report.kind === RoyaltyReportKind.ORIGINAL ? (
      <p className="mt-1 text-xs text-muted-foreground/70">매출 증빙 없음</p>
    ) : null;
  }
  const open = async (fileId: string) => {
    try {
      const url = URL.createObjectURL(await api.royaltyReportFile(token, report.id, fileId));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    }
  };
  return (
    <div className="mt-1 space-y-0.5 text-xs">
      {(['BUYER', 'SELLER'] as const).map((role) => {
        const files = report.files.filter((f) => f.uploaderRole === role);
        if (!files.length) return null;
        return (
          <p key={role} className="flex flex-wrap gap-x-2">
            <span className="text-muted-foreground">
              {role === 'BUYER' ? '구매자 매출 증빙' : '판매자 이의 자료'}
            </span>
            {files.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => void open(f.id)}
                className="max-w-[14rem] truncate font-semibold text-brand hover:underline"
              >
                {f.fileName}
              </button>
            ))}
          </p>
        );
      })}
    </div>
  );
}

/** 정정분은 부호를 붙여 보여 준다 (+ 추가 / − 환급·회수) */
function signed(amount: number, withSign: boolean): string {
  const text = Math.abs(amount).toLocaleString('ko-KR');
  if (!withSign) return text;
  return `${amount >= 0 ? '+' : '−'}${text}`;
}

/**
 * 입금 확인·지급 이후 매출 정정 — 바로잡은 전체 매출과 근거를 받는다.
 * 차액은 지금까지 반영된 금액(원래 보고 + 이전 정정) 기준으로 서버가 계산한다.
 */
function AdjustDialog({
  report,
  busy,
  onSubmit,
  onClose,
}: {
  report: AdminRoyaltyReportResponse;
  busy: boolean;
  onSubmit(body: { correctedRevenue: number; reason: string }): void;
  onClose(): void;
}) {
  const [revenue, setRevenue] = useState('');
  const [reason, setReason] = useState('');
  const corrected = Number(revenue.replace(/[^\d]/g, ''));
  const newRoyalty = Math.floor((corrected * report.royaltyRateBp) / 10_000);
  const ok = revenue.trim() !== '' && reason.trim().length >= 5 && !busy;
  return (
    <Dialog label="로열티 매출 정정" onClose={onClose}>
      <p className="text-lg font-bold tracking-tight">로열티 매출 정정</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {periodLabel(report.period)} · 보고 매출 {report.reportedRevenue.toLocaleString('ko-KR')}원
        · 로열티 {report.royaltyAmount.toLocaleString('ko-KR')}원. 바로잡은 전체 매출을 넣으면
        차액만큼 정정 보고가 생겨요 — 늘면 구매자 추가 입금, 줄면 구매자 환급(이미 지급했다면 판매자
        회수).
      </p>
      <div className="mt-4 space-y-3">
        <label className="block">
          <span className="text-xs font-semibold text-muted-foreground">바로잡은 매출 (원)</span>
          <input
            inputMode="numeric"
            className={`mt-1 w-full tabular-nums ${inputCls}`}
            value={revenue ? corrected.toLocaleString('ko-KR') : ''}
            onChange={(e) => setRevenue(e.target.value.replace(/[^\d]/g, ''))}
            autoFocus
          />
          {revenue && (
            <span className="mt-1 block text-xs text-muted-foreground">
              바로잡은 로열티 {newRoyalty.toLocaleString('ko-KR')}원 (원래 보고 대비{' '}
              {signed(newRoyalty - report.royaltyAmount, true)}원)
            </span>
          )}
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-muted-foreground">
            정정 근거 (5자 이상, 양측에 전달돼요)
          </span>
          <textarea
            rows={3}
            className={`mt-1 w-full resize-none py-2 ${inputCls}`}
            placeholder="예: 구매자가 제출한 9월 매출 증빙(세금계산서 합계) 기준으로 정정"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      </div>
      <div className="mt-5 flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          className="flex-1"
          disabled={!ok}
          onClick={() => onSubmit({ correctedRevenue: corrected, reason: reason.trim() })}
        >
          정정 보고 만들기
        </Button>
      </div>
    </Dialog>
  );
}

/** 정산 대상 월 'YYYY-MM' → '2026년 6월 매출분' (날짜로 오해하지 않게) */
function periodLabel(period: string): string {
  const [year, month] = period.split('-');
  return `${year}년 ${Number(month)}월 매출분`;
}

/** 로열티 당사자 — 이름을 누르면 회원 상세. 역할(구매자·판매자)과 이메일을 함께 보여 준다 */
function PersonLink({
  id,
  name,
  email,
  role,
  dimmed = false,
}: {
  id: string;
  name: string;
  email: string;
  role: string;
  /** 지금 단계에서 덜 중요한 쪽 — 흐리게 */
  dimmed?: boolean;
}) {
  return (
    <span className={`inline-flex min-w-0 items-center gap-1 ${dimmed ? 'opacity-50' : ''}`}>
      <span className="text-muted-foreground">{role}</span>
      <Link
        to="/users/$userId"
        params={{ userId: id }}
        className="font-semibold text-foreground hover:text-brand hover:underline"
      >
        {name || '(이름 없음)'}
      </Link>
      <span className="truncate text-muted-foreground">{email}</span>
    </span>
  );
}

/** 칩 옆 처리 대기 개수 — 개수를 세지 않는 칩(전체·정산 완료·반려됨)은 비워 둔다 */
function ChipCount({ value }: { value: number | undefined }) {
  if (value === undefined) return null;
  return <span className="ml-1.5 tabular-nums">{value.toLocaleString('ko-KR')}</span>;
}
